import * as df from 'date-fns';
import { remove } from 'lodash-es';
import { IntlShape, useIntl } from 'react-intl';
import * as IBooks from '../../../interfaces/merchants/resources/books';
import { dateStringToZonedTime } from '../../../molecules/merchant/calendar/calendarHelper';
import { useContext } from 'react';
import { TimezoneContext } from '../../../contexts/TimezoneContext';

const finder = (name: string) =>
  name
    .replace(/\[(\w+)\]/g, '.$1')
    .replace(/^\./, '')
    .split('.');

const bindDown = (key: string, obj: object) => {
  let found = key in obj ? obj[key] : {};
  found = Array.isArray(found) ? Object.values({ ...found }) : { ...found };
  obj[key] = found;
  return found;
};

const secondsToDaysHoursMinutes = (seconds: number) => {
  const days = Math.floor(seconds / 60 / 60 / 24);
  const daysRest = days * 24 * 60 * 60;
  const hours = Math.floor((seconds - daysRest) / 60 / 60);
  const hoursRest = hours * 60 * 60;
  const minutes = Math.floor((seconds - daysRest - hoursRest) / 60);

  return {
    days,
    hours,
    minutes,
  };
};

// UI的には入らないがtable的にはseconds_from_base_checkinに(60 * 60)で割り切れないものもデータとして持ち得るのでそこを許容するか
const secondsToHoursAndMinutes = (seconds: number) => {
  const hours = Math.floor(seconds / 60 / 60);
  const minutes = Math.floor(seconds / 60);

  return {
    hours,
    minutes,
  };
};

const createReservablePeriod = (
  intl: IntlShape,
  r: IBooks.ReservablePeriodStart | IBooks.ReservablePeriodEnd,
  timezone: string,
): string => {
  if (!r || r.type === 'no_setup') {
    return '';
  }
  if (r.type === 'relative_month') {
    if (!r.relative_month)
      throw new Error(`cannot create ReservablePeriod with ${r}`);
    const { base_months, seconds_from_base_months } = r.relative_month;
    const monthsText =
      base_months === 0
        ? intl.formatMessage({ id: 'books.note.thisMonth' })
        : base_months === 1
        ? intl.formatMessage({ id: 'books.note.lastMonth' })
        : intl.formatMessage(
            { id: 'books.note.beforeMonths' },
            { months: base_months },
          );
    const { days, hours, minutes } = secondsToDaysHoursMinutes(
      seconds_from_base_months,
    );
    return intl.formatMessage(
      { id: 'books.note.reservablePeriod' },
      // dayのみ開始が1日から(hoursとminutesは0から始まる)のでこの修正が要る
      { months: monthsText, days: days + 1, hours, minutes },
    );
  }
  if (r.type === 'relative_day') {
    if (!r.relative_day)
      throw new Error(`cannot create ReservablePeriod with ${r}`);
    const { base_days, seconds_from_base_days } = r.relative_day;

    const { hours, minutes } = secondsToDaysHoursMinutes(
      seconds_from_base_days,
    );

    return intl.formatMessage(
      { id: 'books.note.relativeDay' },
      { days: base_days, hours, minutes },
    );
  }
  if (r.type === 'relative') {
    if (!r.relative)
      throw new Error(`cannot create ReservablePeriod with ${r}`);
    const { seconds_from_base_checkin } = r.relative;

    const { minutes, hours } = secondsToHoursAndMinutes(
      seconds_from_base_checkin,
    );

    if (seconds_from_base_checkin >= 60 * 60) {
      return intl.formatMessage({ id: 'books.note.beforeHours' }, { hours });
    } else {
      return intl.formatMessage(
        { id: 'books.note.beforeMinutes' },
        { minutes },
      );
    }
  }
  if (r.type === 'datetime') {
    if (!r.datetime)
      throw new Error(`cannot create ReservablePeriod with ${r}`);
    const { datetime } = r.datetime;
    const luxonDate = dateStringToZonedTime(datetime, timezone);
    const datetimeStr = luxonDate.toFormat('yyyy/MM/dd');
    const [hours, minutes] = luxonDate.toFormat('H:mm').split(':');

    return intl.formatMessage(
      { id: 'books.note.datetime' },
      { datetime: datetimeStr, hours, minutes },
    );
  }
  throw new Error(`cannot create ReservablePeriod with ${r}`);
};

const createReservableFromNote = (
  intl: IntlShape,
  reservablePeriodStart: IBooks.ReservablePeriodStart,
  timezone: string,
): Note | undefined => {
  const period = createReservablePeriod(intl, reservablePeriodStart, timezone);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveFrom.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.from',
        })}`,
      }
    : undefined;
};

const createReservableUntilNote = (
  intl: IntlShape,
  reservablePeriodEnd: IBooks.ReservablePeriodEnd,
  timezone: string,
): Note | undefined => {
  const period = createReservablePeriod(intl, reservablePeriodEnd, timezone);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveUntil.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.until',
        })}`,
      }
    : undefined;
};

const createChangeableOrCancelablePeriod = (
  intl: IntlShape,
  c: IBooks.ChangeableType | IBooks.CancelableType,
): string => {
  if (!c || c.type !== 'accept') {
    throw new Error(`cannot create Period with ${c}`);
  }
  switch (true) {
    case c.until === 0: {
      return intl.formatMessage({ id: 'books.note.untilReserveDateTime' });
    }
    case c.until < 86400: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByHours' },
        { hours: c.until / 60 / 60 },
      );
    }
    default: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByDays' },
        { days: c.until / 60 / 60 / 24 },
      );
    }
  }
};

const createChangeableDescription = (
  intl: IntlShape,
  changeable: IBooks.ChangeableType,
): string => {
  if (!changeable) {
    return '';
  }
  if (changeable.type === 'not_accept') {
    return intl.formatMessage({ id: 'books.note.changeable.notAccept' });
  }
  if (changeable.type === 'accept') {
    return createChangeableOrCancelablePeriod(intl, changeable);
  }
  throw new Error(`cannot create ChangeableDescription with ${changeable}`);
};

const createChangeableNote = (
  intl: IntlShape,
  changeable: IBooks.ChangeableType,
): Note | undefined => {
  const description = createChangeableDescription(intl, changeable);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.changeable.title' }),
        description,
      }
    : undefined;
};

const createCancelableDescription = (
  intl: IntlShape,
  cancelable: IBooks.CancelableType,
): { description: string; caution?: string } => {
  if (!cancelable) {
    return { description: '' };
  }
  if (cancelable.type === 'not_accept') {
    return {
      description: intl.formatMessage({
        id: 'books.note.cancelable.notAccept',
      }),
    };
  }
  if (cancelable.type === 'accept') {
    const description = createChangeableOrCancelablePeriod(intl, cancelable);
    const caution = cancelable.prepayable
      ? intl.formatMessage(
          { id: 'books.note.cancelable.caution' },
          { days: cancelable.refundableDaysLimit },
        )
      : undefined;
    return { description, caution };
  }
  throw new Error(`cannot create ChangeableDescription with ${cancelable}`);
};

const createCancelableNote = (
  intl: IntlShape,
  cancelable: IBooks.CancelableType,
): Note | undefined => {
  const { description, caution } = createCancelableDescription(
    intl,
    cancelable,
  );
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.cancelable.title' }),
        description,
        caution,
      }
    : undefined;
};

const createOnlineMeetingDescription = (
  intl: IntlShape,
  onlineMeeting: IBooks.OnlineMeetingType,
): string => {
  if (!onlineMeeting || onlineMeeting.auto_create === 'disabled') {
    return '';
  }

  if (onlineMeeting.auto_create === 'enabled') {
    const description = intl.formatMessage({
      id: 'books.note.onlineMeeting.enabled',
    });
    return description;
  }
  throw new Error(`cannot create OnlineDescription with ${onlineMeeting}`);
};

const createOnlineMeetingNote = (
  intl: IntlShape,
  onlineMeeting: IBooks.OnlineMeetingType,
): Note | undefined => {
  const description = createOnlineMeetingDescription(intl, onlineMeeting);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.onlineMeeting.title' }),
        description,
      }
    : undefined;
};

const createRemoteLockDescription = (
  intl: IntlShape,
  remoteLock: IBooks.EnabledRemoteLock,
) => {
  if (remoteLock) {
    return intl.formatMessage({
      id: 'books.note.remoteLock.enabled',
    });
  } else {
    return undefined;
  }
};

const createRemoteLockNote = (
  intl: IntlShape,
  enabledRemoteLock: IBooks.EnabledRemoteLock,
): Note | undefined => {
  const description = createRemoteLockDescription(intl, enabledRemoteLock);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.remoteLock.title' }),
        description,
      }
    : undefined;
};

export const changeObjectByNestedString = (
  obj: object,
  name: string,
  value: unknown,
): object => {
  // nameのstringでvalueをupdateする。
  // nameは `me.firstname` とか `options[0].description` とかで指定できる
  const names = finder(name);
  const newObj = { ...obj };
  let found = newObj;
  for (let i = 0, n = names.length; i < n; ++i) {
    const key = names[i];
    if (i === n - 1) {
      // last
      found[key] = value;
      break;
    }
    found = bindDown(key, found);
    if (i === 0) {
      // first
      newObj[key] = found;
    }
  }
  return newObj;
};

// tslint:disable-next-line: no-any
export const getValueByNestedString = (obj: object, name: string) => {
  const names = finder(name);
  let found = obj;
  for (let i = 0, n = names.length; i < n; ++i) {
    const key = names[i];
    if (i === n - 1) {
      // last
      return found[key];
    }
    found = key in found ? found[key] : {};
  }
  return undefined;
};

export const isRandomStaff = (s: IBooks.Staff): s is IBooks.RandomStaff =>
  s.id === '';

interface Note {
  title: string;
  description: string;
  caution?: string;
}

export const useResourceNotes = ({
  reservable_period,
  changeable,
  cancelable,
  onlineMeeting,
  enabledRemoteLock,
}: IBooks.SettingConfirmation | IBooks.SettingCompletion): Note[] => {
  const intl = useIntl();
  const context = useContext(TimezoneContext);
  const { start_period, end_period } = reservable_period;
  return [
    createReservableFromNote(intl, start_period, context.timezone),
    createReservableUntilNote(intl, end_period, context.timezone),
    createChangeableNote(intl, changeable),
    createCancelableNote(intl, cancelable),
    createOnlineMeetingNote(intl, onlineMeeting),
    createRemoteLockNote(intl, enabledRemoteLock),
  ].filter((n) => !!n) as Note[];
};

export function radioBehaviorByCheckbox<T extends { id: number | string }>(
  mode: 'single' | 'multiple',
  checked: boolean,
  elements: T[],
  newElement: T,
): T[] {
  if (mode === 'single') {
    return checked ? [newElement] : [];
  }

  checked
    ? elements.push(newElement)
    : remove(elements, (e) => e.id === newElement.id);
  return elements;
}

export const getCalendarDate = (date: Date) => {
  const startDate = df.startOfMonth(date);
  const endDate = df.lastDayOfMonth(date);
  const endDayCount = endDate.getDate();
  const lastMonthEndDate = df.addMonths(endDate, -1);
  const lastMonthendDayCount = lastMonthEndDate.getDate();
  const startDay = startDate.getDay();
  const previous = df.addMonths(date, -1);
  const next = df.addMonths(date, 1);
  let dayCount = 1;

  const dates = [];
  for (let w = 0; w < 6; w++) {
    for (let d = 0; d < 7; d++) {
      let type = 'current';
      let stringDate = '';
      let datenum = 0;
      if (w === 0 && d < startDay) {
        // 1行目で1日の曜日の前
        datenum = lastMonthendDayCount - startDay + d + 1;
        stringDate = [
          previous.getFullYear(),
          `0${previous.getMonth() + 1}`.slice(-2),
          `0${datenum}`.slice(-2),
        ].join('-');
        type = 'previous';
      } else if (dayCount > endDayCount) {
        // 末尾の日数を超えた
        datenum = dayCount - endDayCount;
        stringDate = [
          next.getFullYear(),
          `0${next.getMonth() + 1}`.slice(-2),
          `0${datenum}`.slice(-2),
        ].join('-');
        type = 'next';
        dayCount++;
      } else {
        datenum = dayCount;
        stringDate = [
          date.getFullYear(),
          `0${date.getMonth() + 1}`.slice(-2),
          `0${dayCount}`.slice(-2),
        ].join('-');

        dayCount++;
      }
      dates.push({ date: datenum, iso: stringDate, type });
    }
  }
  return dates;
};
